import ParameterDraggingComponent from "@AppBuilderLib/entities/parameter/ui/ParameterDraggingComponent";
import ParameterDrawingComponent from "@AppBuilderLib/entities/parameter/ui/ParameterDrawingComponent";
import ParameterGumballComponent from "@AppBuilderLib/entities/parameter/ui/ParameterGumballComponent";
import ParameterRectangleTransformComponent from "@AppBuilderLib/entities/parameter/ui/ParameterRectangleTransformComponent";
import ParameterSelectionComponent from "@AppBuilderLib/entities/parameter/ui/ParameterSelectionComponent";
import ViewportAnchor2d, {
	ViewportAnchor2dThemeProps,
} from "@AppBuilderLib/entities/viewport-anchor/ui/ViewportAnchor2d";
import ViewportAnchor3d, {
	ViewportAnchor3dThemeProps,
} from "@AppBuilderLib/entities/viewport-anchor/ui/ViewportAnchor3d";
import ViewportComponent from "@AppBuilderLib/entities/viewport/ui/ViewportComponent";
import ViewportOverlayWrapper from "@AppBuilderLib/entities/viewport/ui/ViewportOverlayWrapper";
import {IComponentContext} from "@AppBuilderLib/features/appbuilder/config/ComponentContext.types";
import {
	AppBuilderContainerNameType,
	isArAction,
	isAttributeVisualizationWidget,
	isCameraAction,
	isFullscreenAction,
	isSceneTreeExplorerWidget,
} from "@AppBuilderLib/features/appbuilder/config/appbuilder";
import AppBuilderActionArComponent from "@AppBuilderLib/features/appbuilder/ui/AppBuilderActionArComponent";
import AppBuilderActionCameraComponent from "@AppBuilderLib/features/appbuilder/ui/AppBuilderActionCameraComponent";
import AppBuilderActionFullscreenComponent from "@AppBuilderLib/features/appbuilder/ui/AppBuilderActionFullscreenComponent";
import AppBuilderToolbarLayer from "@AppBuilderLib/features/appbuilder/ui/AppBuilderToolbarLayer";
import RootComponent from "@AppBuilderLib/shared/ui/root/RootComponent";
import AppBuilderAttributeVisualizationWidgetComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderAttributeVisualizationWidgetComponent";
import AppBuilderContainerComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderContainerComponent";
import AppBuilderFallbackContainerComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderFallbackContainerComponent";
import AppBuilderSceneTreeExplorerWidgetComponent from "@AppBuilderLib/widgets/appbuilder/ui/AppBuilderSceneTreeExplorerWidgetComponent";
import {NumberAttributeThemeProps} from "@AppBuilderLib/widgets/appbuilder/ui/attributes/NumberAttribute";
import {StringAttributeThemeProps} from "@AppBuilderLib/widgets/appbuilder/ui/attributes/StringAttribute";
import {PARAMETER_TYPE} from "@shapediver/viewer.session";
import "instruments/sentry";
import ReactDOM from "react-dom/client";
import AppBuilderBase from "~/AppBuilderBase";
import {PlausibleTracker} from "~/instruments/plausible";
import {setupWebVitalsTracking} from "~/instruments/webvitals";
import {SentryErrorReportingContext} from "./instruments/sentry";
import EidosMappaComponent from "~/eidos/EidosMappaComponent";
import EidosSceltaComponent from "~/eidos/EidosSceltaComponent";
import EidosFotoComponent from "~/eidos/EidosFotoComponent";
import EidosNumeroComponent from "~/eidos/EidosNumeroComponent";
// EIDOS: regole per il totem del negozio (schermo touch in verticale) e ritorno all'inizio dopo inattivita'
import "~/eidos/totem.css";
import {avviaTotem} from "~/eidos/totem";
import EidosTotem from "~/eidos/EidosTotem";
import EidosVista from "~/eidos/EidosVista";
import EidosSalvaTagli from "~/eidos/EidosSalvaTagli";
import EidosDirittiFoto from "~/eidos/EidosDirittiFoto";
import EidosAvvisoRetro from "~/eidos/EidosAvvisoRetro";
import {avviaScritturaTelefono} from "~/eidos/tabsTelefono";
// EIDOS (29 ott 2026): la riga «Manca ancora: …» del riquadro prezzo in evidenza (riquadro ottanio chiaro)
import {avviaMancaAncora} from "~/eidos/mancaAncora";

avviaTotem();
// EIDOS (24 ott 2026): sul telefono, mentre si scrive, il 3D si ritira e la casella resta sopra la tastiera
avviaScritturaTelefono();
avviaMancaAncora();

const root = ReactDOM.createRoot(
	document.getElementById("root") as HTMLElement,
);

const components: IComponentContext = {
	viewportAnchors: {
		[AppBuilderContainerNameType.Anchor2d]: {
			component: ViewportAnchor2d,
			themeProps: ViewportAnchor2dThemeProps,
		},
		[AppBuilderContainerNameType.Anchor3d]: {
			component: ViewportAnchor3d,
			themeProps: ViewportAnchor3dThemeProps,
		},
	},
	viewportComponent: {component: ViewportComponent},
	viewportOverlayWrapper: {component: ViewportOverlayWrapper},
	appBuilderToolbarLayer: {component: AppBuilderToolbarLayer},
	parameters: {
		// EIDOS: il campo di testo "Indirizzo" diventa il pannello mappa con suggerimenti e trascinamento
		[PARAMETER_TYPE.STRING]: {
			component: EidosMappaComponent,
			extraBottomPadding: false,
		},
		// EIDOS: i menu "Quale indirizzo" e "Posizione del punto" sono nascosti (li sostituisce la mappa)
		[PARAMETER_TYPE.STRINGLIST]: {
			component: EidosSceltaComponent,
			extraBottomPadding: false,
		},
		// EIDOS (Gioiello Sagoma): il file "Foto" diventa il pannello con la foto trascinabile sotto la maschera
		[PARAMETER_TYPE.FILE]: {
			component: EidosFotoComponent,
			extraBottomPadding: false,
		},
		// EIDOS (Gioiello Sagoma): i cursori Zoom/Sposta sono nascosti, li muove il pannello foto
		[PARAMETER_TYPE.INT]: {
			component: EidosNumeroComponent,
			extraBottomPadding: false,
		},
		[PARAMETER_TYPE.FLOAT]: {
			component: EidosNumeroComponent,
			extraBottomPadding: false,
		},
		[PARAMETER_TYPE.DRAWING]: {
			component: ParameterDrawingComponent,
			extraBottomPadding: true,
		},
		[PARAMETER_TYPE.INTERACTION]: {
			selection: {
				component: ParameterSelectionComponent,
				extraBottomPadding: true,
			},
			gumball: {
				component: ParameterGumballComponent,
				extraBottomPadding: true,
			},
			dragging: {
				component: ParameterDraggingComponent,
				extraBottomPadding: false,
			},
			rectangleTransform: {
				component: ParameterRectangleTransformComponent,
				extraBottomPadding: true,
			},
		},
	},
	widgets: {
		attributeVisualization: {
			isComponent: isAttributeVisualizationWidget,
			component: AppBuilderAttributeVisualizationWidgetComponent,
			themeProps: {
				NumberAttribute: NumberAttributeThemeProps,
				StringAttribute: StringAttributeThemeProps,
			},
		},
		sceneTreeExplorer: {
			isComponent: isSceneTreeExplorerWidget,
			component: AppBuilderSceneTreeExplorerWidgetComponent,
		},
	},
	actions: {
		ar: {
			isAction: isArAction,
			component: AppBuilderActionArComponent,
		},
		camera: {
			isAction: isCameraAction,
			component: AppBuilderActionCameraComponent,
		},
		fullscreen: {
			isAction: isFullscreenAction,
			component: AppBuilderActionFullscreenComponent,
		},
	},
	containerComponent: AppBuilderContainerComponent,
	fallbackContainerComponent: AppBuilderFallbackContainerComponent,
};

root.render(
	<RootComponent
		useStrictMode={false}
		tracker={PlausibleTracker}
		errorReporting={SentryErrorReportingContext}
		componentContext={components}
	>
		<AppBuilderBase />
		{/* EIDOS: pulsante CENTRA, camera senza trascinamento e tastiera sullo schermo (solo con ?totem=…) */}
		<EidosTotem />
		{/* EIDOS: il gioiello si gira sul retro quando si scrive la dedica e torna dritto cambiando le altre scelte */}
		<EidosVista />
		{/* EIDOS (6 ott 2026): pulsante SALVA TAGLI sotto il riquadro prezzo: DXF divisi (esterno / interni) in uno zip via email a Eidos */}
		<EidosSalvaTagli />
		{/* EIDOS (14 ott 2026): Týpos, casella "dichiaro di avere i diritti sull'immagine" obbligatoria sopra il carrello */}
		<EidosDirittiFoto />
		{/* EIDOS (16 ott 2026): Týpos, avviso rosso sotto il testo del retro (limite di caratteri / testo che non sta) */}
		<EidosAvvisoRetro />
	</RootComponent>,
);

PlausibleTracker.trackPageview();
setupWebVitalsTracking(PlausibleTracker);
